import React, { useEffect, useState, useMemo } from "react";
import { Formik } from "formik";
import "./DebtorBasicInfo.scss";
import { ConnectedProps, connect } from "react-redux";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import {
  BoxIcons,
  IconTypes,
  SwapOutlined,
  EnvironmentFilled,
  PhoneFilled,
  DownOutlined,
  UpOutlined,
  TeamOutlined,
} from "us.icons";
import {
  CaseList,
  CreditInformation,
} from "us.collection.debtor/components/DebtorBasicInfo/Components";
import {
  getAddressByDebtorDetails,
  getPhoneNumbersByDebtorDetails,
  isHistorySaved,
} from "us.collection.debtor/components/DebtorBasicInfo/Functions";
import { filterDuplicateDebtors } from "us.collection.debtor/components/DebtorBasicInfo/Components/DuplicateDebtors/Functions";
import { DEFAULT_DUPLICATE_DEBTOR_FILTERS } from "us.collection.debtor/components/DebtorBasicInfo/Components/DuplicateDebtors/Constants";
import { IDebtorBasicInfo } from "us.collection.debtor/components/Interfaces";
import EditOtherParty from "us.collection.case/components/OtherParty/Edit/Home";
import _ from "lodash";
import * as Actions from "us.collection.case/actions";
import { IOtherPartySummery } from "us.collection.case/reducers/Dashboard/Interfaces";
import { appInsights } from "us.helper";
import * as action_ from "us.collection.debtor/actions";
import { $SelectDebtor } from "us.common/components/SelectDebtor";
import { isSubCaseTransferredToCase } from "us.common/functions";
import { RootState } from "us.helper/types";
import { SaveSearchHistory } from "us.common/repository";
import { useNavigate, useLocation } from "react-router-dom";

const {
  $Button,
  $Typography,
  $Tooltip,
  $Skeleton,
  $Drawer,
  $List,
  $Radio,
  $Badge,
} = Common.Components;
const { Paragraph } = $Typography;
const { OtherPartyAction } = Actions;
const { otherParty, otherParties } = OtherPartyAction;

const DebtorBasicInfo: React.FC<IDebtorBasicInfo & PropsFromRedux> = (
  props
) => {
  const { t } = useTranslation();
  const [otherPartyList, setOtherPartyList] = useState<
    IOtherPartySummery[] | undefined
  >([]);

  const navigate = useNavigate();
  const location = useLocation();

  const [caseListVisible, setCaseListVisible] = useState<boolean>(false);
  const [creditInformationVisible, setCreditInformationVisible] =
    useState<boolean>(false);
  const [selectedOtherParty, setSelectedOtherParty] = useState({});

  const {
    metaData,
    debtorWidgetData,
    addDrawer,
    debtorPermission,
    debtorDrawer,
    searchHistory,
    duplicatingDebtors,
    manageAddDrawer,
    onMaximize,
    openDebtorDrawer,
    saveDebtor,
    saveSearchHistory,
    getDuplicateDebtorsData,
  } = props;

  const { otherParties, debtorDetails, isBadDebtor } =
    debtorWidgetData.data ?? {};
  const {
    address1,
    address2,
    address3,
    zipCode,
    zipName,
    phoneNumbers,
    idNo,
    entRoleId,
    debtorType,
  } = debtorDetails ?? {};
  const { cid, caseId, caseNo, debtorId, isAddressKnown } = metaData.data ?? {};

  useEffect(() => {
    if (
      !metaData.isLoading &&
      debtorWidgetData &&
      caseId &&
      !isHistorySaved(caseNo, entRoleId, searchHistory) &&
      debtorDetails &&
      caseNo && 
      metaData.data?.entityType
    ) {
      try {
        // save search data
        const payload = SaveSearchHistory.call({
          ...debtorDetails,
          ...metaData.data,
        });
        saveSearchHistory && saveSearchHistory(payload);
      } catch (error) {
        // Log the error but don't break the application
        // This prevents bad requests when required fields are missing
        appInsights.trackException(
          `SaveSearchHistory validation failed: ${error}. Data: caseNo=${caseNo}, entityType=${metaData.data?.entityType}`
        );
      }
    }
  }, [debtorWidgetData]);

  useEffect(() => {
    if (otherParties) {
      const firstOtherParty = _.orderBy(
        otherParties && otherParties.filter((item: any) => item.activeStatus),
        ["regDate"],
        ["desc"]
      )[0];
      if (typeof firstOtherParty !== "undefined") {
        setOtherPartyList([firstOtherParty]);
      }
    }
  }, [otherParties]);

  useEffect(() => {
    debtorId &&
      getDuplicateDebtorsData &&
      getDuplicateDebtorsData({ debtorId });
  }, [metaData.data]);

  const allDuplicatingDebtors = useMemo(() => {
    return filterDuplicateDebtors(
      duplicatingDebtors.data,
      DEFAULT_DUPLICATE_DEBTOR_FILTERS
    );
  }, [duplicatingDebtors.data]);

  const onLoadingMore = () => {
    try {
      setOtherPartyList(
        _.orderBy(
          otherParties?.filter((item: any) => item.activeStatus),
          ["regDate"],
          ["desc"]
        )
      );
    } catch (error) {
      appInsights.trackException(
        `Dashboard Debtor OtherParty loading more Exeception - ${error}`
      );
    }
  };

  const onCollapse = () => {
    try {
      setOtherPartyList([
        _.orderBy(
          otherParties?.filter((item: any) => item.activeStatus),
          ["regDate"],
          ["desc"]
        )[0],
      ]);
    } catch (error) {
      appInsights.trackException(
        `Dashboard Debtor OtherParty Collapse Exeception - ${error}`
      );
    }
  };
  const loadMore = otherParties && (
    <div className="load-more" onClick={onLoadingMore}>
      <DownOutlined />
    </div>
  );

  const minimize = otherParties && (
    <div className="load-more" onClick={onCollapse}>
      <UpOutlined />
    </div>
  );

  const onOtherParty = (item: any) => {
    manageAddDrawer && manageAddDrawer({ title: "", visible: true });
    setSelectedOtherParty(item);
  };

  const OtherPartyClose = () => {
    manageAddDrawer && manageAddDrawer({ title: "", visible: false });
  };

  const handleChangeDebtorDrawer = (visible: boolean) => {
    openDebtorDrawer &&
      openDebtorDrawer({
        title: visible ? t("US.COLLECTION.DEBTOR:BASICINFO.SELECT_DEBTOR") : "",
        visible,
      });
  };

  const changeDebtorDetails = (selectedDebtor: any, isAddDebtor?: boolean) => {
    const metaDataObject = {
      id: caseNo,
      type: "caseno",
    };
    if (isAddDebtor) {
      saveDebtor &&
        saveDebtor({
          newDebtorEntRoleId: -1,
          newCustId: selectedDebtor.creditorReference,
          caseId,
          metaDataObject,
          isAddDebtor,
        });
    } else {
      saveDebtor &&
        saveDebtor({
          newDebtorEntRoleId: selectedDebtor.debtorEntRoleId,
          newCustId: selectedDebtor.custId,
          caseId,
          metaDataObject,
          isAddDebtor,
        });
    }
  };

  const debtorInfoRadioButtons = [
    {
      value: "creditInformation",
      component: t("US.COLLECTION.DEBTOR:BASICINFO.CREDIT_INFORMATION"),
      dataTestId: "dashboard-debtor-caseInformation",
    },
    {
      value: "caseList",
      component: (
        <>
          {t("US.COLLECTION.DEBTOR:BASICINFO.CASE_LIST")}
          <strong>
            {` ${
              (_.find(debtorDetails?.tags, {
                tag: "ActiveCaseCount",
              }) ?? {})["value"]
            }`}
          </strong>
        </>
      ),
      dataTestId: "dashboard-debtor-caseList",
    },
  ];

  /**
   *
   * @description
   * Get proper icon for debtor
   */
  const getDebtorIcon = () => {
    if (isAddressKnown) {
      return (
        <BoxIcons
          type={IconTypes.BOX_ICON}
          name={"debtor"}
          className={
            isBadDebtor?.isBadDebtor ? "baddebtor-icon" : "debtor-icon"
          }
        />
      );
    } else {
      return (
        <$Tooltip
          title={t("US.COLLECTION.DEBTOR:BASICINFO.DEBTOR_ADDRESS_UNKNOWN")}
        >
          <div>
            <BoxIcons
              type={IconTypes.BOX_ICON}
              name={"address-unknown"}
              className={"address-unknown-icon"}
            />
          </div>
        </$Tooltip>
      );
    }
  };

  return (
    <Formik initialValues={{}} onSubmit={() => {}}>
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        ...rest
      }: any) => (
        <React.Fragment>
          <div className="widget widget-debtor-info">
            <div
              className="widget-header widget-link-header d-flex align-items-center px-0"
              onClick={onMaximize}
              data-testid="dashboard-debtor"
            >
              <span className="widget-title">
                {t("US.COLLECTION.DEBTOR:BASICINFO.DEBTOR")}
              </span>
            </div>
            <$Skeleton
              loading={debtorWidgetData.isLoading || metaData.isLoading}
              active
              paragraph={{ rows: 2 }}
            >
              <div className="widget-body px-0">
                <div className="d-flex align-items-center mb-2">
                  {getDebtorIcon()}
                  <div className="ml-2">
                    <strong className="d-block">
                      {cid}
                      <$Tooltip
                        placement="topLeft"
                        title={t("US.COLLECTION.DEBTOR:BASICINFO.CHANGE")}
                      >
                        <$Button
                          className="ml-2"
                          size="small"
                          onClick={() => handleChangeDebtorDrawer(true)}
                          data-testid="dashboard-debtor-changeDebtor"
                          disabled={
                            metaData.data &&
                            isSubCaseTransferredToCase(metaData.data)
                          }
                        >
                          <SwapOutlined />
                        </$Button>
                      </$Tooltip>
                      {allDuplicatingDebtors?.length - 1 > 0 && (
                        <$Tooltip
                          placement="topLeft"
                          title={t(
                            "US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.DUPLICATE_DEBTORS"
                          )}
                        >
                          <$Badge
                            count={allDuplicatingDebtors?.length - 1}
                            style={{
                              backgroundColor: "#ffc248",
                              color: "#000",
                            }}
                          >
                            <$Button
                              className="ml-2"
                              size="small"
                              onClick={() => {
                                caseNo &&
                                  navigate({
                                    ...location,
                                    pathname: `/case/${caseNo}/debtor-information`,
                                    search: "?tabKey=3",
                                  });
                              }}
                            >
                              <TeamOutlined />
                            </$Button>
                          </$Badge>
                        </$Tooltip>
                      )}
                    </strong>
                    <strong className="d-block">
                      {`${debtorDetails?.firstName} ${debtorDetails?.lastName}`}
                    </strong>
                  </div>
                </div>
                <div className="mb-2">
                  <$Radio
                    name="debtorInfoRadio"
                    radioButton="true"
                    options={
                      debtorPermission &&
                      debtorPermission["creditInformation"]?.isEnabled
                        ? debtorInfoRadioButtons
                        : _.filter(
                            debtorInfoRadioButtons,
                            (component: any) =>
                              component.value != "creditInformation"
                          )
                    }
                    optionValue="value"
                    optionContent={(item: any) => item.component}
                    onChange={(e: any) => {
                      e.target.value === "creditInformation" &&
                        setCreditInformationVisible(true);
                      e.target.value === "caseList" && setCaseListVisible(true);
                    }}
                    className="wdi-radio-group"
                  />
                </div>
                {(address1 || address2 || address3 || zipCode || zipName) && (
                  <div className="d-flex wdi-item">
                    <span className="bui-label">
                      <EnvironmentFilled />
                    </span>
                    <Paragraph
                      ellipsis={{
                        rows: 3,
                      }}
                    >
                      {getAddressByDebtorDetails(debtorDetails)}
                    </Paragraph>
                  </div>
                )}
                {phoneNumbers && phoneNumbers.length > 0 && (
                  <$Tooltip
                    placement="topLeft"
                    title={`${t("US.COLLECTION.CASE:SALARY.PHONENO")}`}
                  >
                    <div className="d-flex wdi-item">
                      <span className="bui-label">
                        <PhoneFilled />
                      </span>
                      <Paragraph
                        ellipsis={{
                          rows: 2,
                        }}
                      >
                        {getPhoneNumbersByDebtorDetails(debtorDetails)}
                      </Paragraph>
                    </div>
                  </$Tooltip>
                )}

                {idNo && (
                  <$Tooltip
                    placement="topLeft"
                    title={`${t("US.COLLECTION.CASE:OTHERPARTY.PERSONNO")}`}
                  >
                    <div className="d-flex wdi-item">
                      <span className="bui-label">
                        <BoxIcons
                          type={IconTypes.BOX_ICON}
                          name="personNumber"
                        />
                      </span>
                      <Paragraph ellipsis>{idNo}</Paragraph>
                    </div>
                  </$Tooltip>
                )}

                {/* otherParties */}
                {otherPartyList && otherPartyList.length > 0 && (
                  <div className="mb-3">
                    <$List
                      itemLayout="horizontal"
                      loadMore={
                        otherParties &&
                        otherParties.filter((item: any) => item.activeStatus)
                          ?.length > 1
                          ? otherPartyList?.length === 1
                            ? loadMore
                            : minimize
                          : null
                      }
                      dataSource={otherPartyList}
                      renderItem={(item: any) => {
                        const { entRoleId, roleType, name } = item;
                        return (
                          <$List.Item
                            key={entRoleId}
                            className="other-party-item"
                            onClick={() => onOtherParty(item)}
                          >
                            <$List.Item.Meta
                              className="align-items-center"
                              avatar={
                                <BoxIcons
                                  type={IconTypes.BOX_ICON}
                                  name="other-party"
                                  className="other-party-icon"
                                />
                              }
                              title={`${entRoleId} - ${name}`}
                              description={roleType}
                            />
                          </$List.Item>
                        );
                      }}
                    />
                  </div>
                )}
                <div>
                  <$SelectDebtor
                    name="selectedDebtor"
                    onDrawerClose={() => handleChangeDebtorDrawer(false)}
                    onSelect={(selectedDebtor: object) =>
                      changeDebtorDetails(selectedDebtor)
                    }
                    title={debtorDrawer.title}
                    visible={debtorDrawer.visible}
                    addNewDebtor={(debtorDetails: any) =>
                      changeDebtorDetails(debtorDetails, true)
                    }
                  />
                </div>
              </div>
            </$Skeleton>
          </div>

          <$Drawer
            title={t("US.COLLECTION.DEBTOR:BASICINFO.DEBTOR_CASE_LIST")}
            width={"90%"}
            open={caseListVisible}
            onClose={() => {
              rest.setFieldValue("debtorInfoRadio", undefined);
              setCaseListVisible(false);
            }}
            className="case-list-drawer"
          >
            {
              <CaseList
                {...props}
                debtorType={debtorType}
                onClose={() => {
                  rest.setFieldValue("debtorInfoRadio", undefined);
                  setCaseListVisible(false);
                }}
              />
            }
          </$Drawer>

          <$Drawer
            title={t("US.COLLECTION.DEBTOR:BASICINFO.CREDIT_INFORMATION")}
            width={1300}
            open={creditInformationVisible}
            onClose={() => {
              rest.setFieldValue("debtorInfoRadio", undefined);
              setCreditInformationVisible(false);
            }}
          >
            {
              <CreditInformation
                entRoleId={metaData.data?.debtorEntNumber}
                {...props}
              />
            }
          </$Drawer>

          <$Drawer
            title={t("US.COLLECTION.CASE:OTHERPARTY.EDIT_OTHER_PARTY")}
            width={"auto"}
            className="other-party-edit"
            open={addDrawer?.visible}
            onClose={OtherPartyClose}
            destroyOnClose
          >
            <EditOtherParty
              selectedOtherParty={selectedOtherParty}
              {...props}
            />
          </$Drawer>
        </React.Fragment>
      )}
    </Formik>
  );
};

const { debtor } = action_.changeDebtorActions;

const mapState = (state: RootState) => {
  const {
    dashboard,
    otherParty,
    changeDebtor,
    mainSearch,
    domainView,
    duplicateDebtors,
  } = state;
  const { addDrawer, otherParties } = otherParty;
  const { debtorWidgetData } = dashboard;
  const { debtorDrawer } = changeDebtor;
  const { duplicatingDebtors } = duplicateDebtors;
  const { history } = mainSearch;
  const { metaData } = domainView;

  return {
    metaData,
    debtorWidgetData,
    addDrawer,
    otherParties,
    debtorDrawer,
    searchHistory: history.data,
    duplicatingDebtors,
  };
};

const { history } = Common.Actions.MainSearchActions;
const { debtors } = action_.duplicateDebtors;

const mapDispatch = {
  manageAddDrawer: otherParty.openDrawer,
  getOtherParies: otherParties.get,
  openDebtorDrawer: debtor.openDrawer,
  saveDebtor: debtor.save,
  saveSearchHistory: history.save,
  getDuplicateDebtorsData: debtors.search,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DebtorBasicInfo);
